/* eslint-disable @reatom/unit-naming-rule */

import { action, atom, reatomSet, withAssign } from '@reatom/framework'
import { match } from 'ts-pattern'

import { Result, Array } from '@blade/std-lib'
import { selectedNetworkAtom } from '@root/entities/network'
import { reatomDataTable } from '@root/shared/ui-kit'

import {
  cmpByAssetsCountAsc,
  cmpByAssetsCountDesc,
  cmpByTotalBalanceUsdAsc,
  cmpByTotalBalanceUsdDesc,
} from '../model/user-profile-holdings-wallet'
import { holdingsTableColumns } from '../ui/holdings-table-columns'

import { holdingsRequestAction } from './holdings-request-action'

import type * as UserProfileHoldingsWallet from '../model/user-profile-holdings-wallet'
import type { DelugeId } from '@root/entities/user-short'
import type { TableData } from '@root/shared/ui-kit'

export const reatomUserProfileHoldingsTable = (args: {
  delugeId: DelugeId
  smallScreen?: boolean
}) => {
  const paramsAtom = atom((ctx) => {
    return {
      network: ctx.spy(selectedNetworkAtom),
    }
  }, 'paramsAtom')
  const columns = holdingsTableColumns.map((column) => {
    return {
      ...column,
      minWidth: args.smallScreen ? 100 : 140,
    }
  })

  const tableModel = reatomDataTable({
    columns,
    getId: (it: UserProfileHoldingsWallet.UserProfileHoldingsWallet) =>
      it.address,
    paramsAtom,
    name: 'user-profile-holdings-table',
    loadItems: async (ctx, tableParams) => {
      const { sorting } = tableParams

      const cmp = match(sorting)
        .with({ sortColumn: 'balance' }, ({ sortDirection }) =>
          sortDirection === 'asc'
            ? cmpByTotalBalanceUsdAsc
            : cmpByTotalBalanceUsdDesc,
        )
        .with({ sortColumn: 'assets' }, ({ sortDirection }) =>
          sortDirection === 'asc' ? cmpByAssetsCountAsc : cmpByAssetsCountDesc,
        )
        .otherwise(() => undefined)

      const result = await holdingsRequestAction(ctx, {
        network: tableParams.params.network,
        delugeId: args.delugeId,
      })

      const mappedResult = result.pipe(
        Result.map((data) => {
          const sortedData = cmp ? Array.sort(cmp)(data) : data

          const tableData: TableData<UserProfileHoldingsWallet.UserProfileHoldingsWallet> =
            {
              data: sortedData,
              hasNext: false,
            }

          return tableData
        }),
      )

      return mappedResult
    },
  })

  const expandedWalletsAtom =
    reatomSet<UserProfileHoldingsWallet.UserProfileHoldingsWallet>()
  const toggleExpandedWalletAction = action(
    (ctx, wallet: UserProfileHoldingsWallet.UserProfileHoldingsWallet) => {
      expandedWalletsAtom.toggle(ctx, wallet)
    },
  )
  const clearExpandedWalletsAction = action((ctx) => {
    expandedWalletsAtom.clear(ctx)
  })

  return tableModel.pipe(
    withAssign(() => ({
      expandedWallets: expandedWalletsAtom,
      toggleExpandedWallet: toggleExpandedWalletAction,
      clearExpandedWallets: clearExpandedWalletsAction,
    })),
  )
}

export type UserProfileHoldingsTableModel = ReturnType<
  typeof reatomUserProfileHoldingsTable
>
